import { z } from 'zod';

export const MemberRoleSchema = z.enum([
  'NETWORK_ADMIN',
  'SCHOOL_DIRECTOR',
  'COORDINATOR',
  'TEACHER',
  'VIEWER',
]);
export type MemberRole = z.infer<typeof MemberRoleSchema>;

/**
 * PLATFORM_ADMIN (equipe TaskAll) nunca vem de membership: é derivado do e-mail verificado
 * no domínio da plataforma (@taskall.ai) ou do custom claim { role: 'PLATFORM_ADMIN' }.
 */
export const RoleSchema = z.enum(['PLATFORM_ADMIN', ...MemberRoleSchema.options]);
export type Role = z.infer<typeof RoleSchema>;

export const ROLE_LABELS: Record<Role, string> = {
  PLATFORM_ADMIN: 'Equipe TaskAll',
  NETWORK_ADMIN: 'Administrador de Rede',
  SCHOOL_DIRECTOR: 'Diretora',
  COORDINATOR: 'Coordenação',
  TEACHER: 'Professor(a)',
  VIEWER: 'Leitura',
};

export const ActiveStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export type ActiveStatus = z.infer<typeof ActiveStatusSchema>;

export const StudentStatusSchema = z.enum(['ACTIVE', 'PENDING', 'INACTIVE']);
export type StudentStatus = z.infer<typeof StudentStatusSchema>;

export const STUDENT_STATUS_LABELS: Record<StudentStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING: 'Pendente',
  INACTIVE: 'Inativo',
};

export const ShiftSchema = z.enum(['MORNING', 'AFTERNOON', 'EVENING', 'FULL_TIME']);
export type Shift = z.infer<typeof ShiftSchema>;

export const SHIFT_LABELS: Record<Shift, string> = {
  MORNING: 'Matutino',
  AFTERNOON: 'Vespertino',
  EVENING: 'Noturno',
  FULL_TIME: 'Integral',
};

export const ClassStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
export type ClassStatus = z.infer<typeof ClassStatusSchema>;

export const EducationLevelSchema = z.enum([
  'EARLY_CHILDHOOD',
  'ELEMENTARY_1',
  'ELEMENTARY_2',
  'HIGH_SCHOOL',
]);
export type EducationLevel = z.infer<typeof EducationLevelSchema>;

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  EARLY_CHILDHOOD: 'Educação Infantil',
  ELEMENTARY_1: 'Ensino Fundamental 1',
  ELEMENTARY_2: 'Ensino Fundamental 2',
  HIGH_SCHOOL: 'Ensino Médio',
};

export const AdministrativeSectorSchema = z.enum(['PUBLIC', 'PRIVATE']);
export type AdministrativeSector = z.infer<typeof AdministrativeSectorSchema>;

export const EnrollmentStatusSchema = z.enum(['ACTIVE', 'TRANSFERRED', 'CANCELLED', 'COMPLETED']);
export type EnrollmentStatus = z.infer<typeof EnrollmentStatusSchema>;

export const AttendanceStatusSchema = z.enum(['PRESENT', 'ABSENT', 'JUSTIFIED']);
export type AttendanceStatus = z.infer<typeof AttendanceStatusSchema>;

export const FeedbackCategorySchema = z.enum(['PRAISE', 'ATTENTION', 'PEDAGOGICAL', 'OTHER']);
export type FeedbackCategory = z.infer<typeof FeedbackCategorySchema>;

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  PRAISE: 'Elogio',
  ATTENTION: 'Atenção',
  PEDAGOGICAL: 'Pedagógico',
  OTHER: 'Outro',
};

export const EventTypeSchema = z.enum(['ACADEMIC', 'HOLIDAY', 'ADMINISTRATIVE']);
export type EventType = z.infer<typeof EventTypeSchema>;

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  ACADEMIC: 'Letivos / Acadêmicos',
  HOLIDAY: 'Feriados / Recessos',
  ADMINISTRATIVE: 'Administrativo / Reuniões',
};

export const ReportTypeSchema = z.enum([
  'CLASS_PERFORMANCE',
  'ATTENDANCE',
  'DROPOUT_RISK',
  'STUDENT_TRANSCRIPT',
  'SCHOOL_OVERVIEW',
]);
export type ReportType = z.infer<typeof ReportTypeSchema>;

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  CLASS_PERFORMANCE: 'Desempenho da Turma',
  ATTENDANCE: 'Frequência Consolidada',
  DROPOUT_RISK: 'Evasão Escolar e Alunos em Risco',
  STUDENT_TRANSCRIPT: 'Boletim Escolar',
  SCHOOL_OVERVIEW: 'Visão Geral da Escola',
};

export const ReportFormatSchema = z.enum(['PDF', 'XLSX']);
export type ReportFormat = z.infer<typeof ReportFormatSchema>;

export const ReportStatusSchema = z.enum(['QUEUED', 'GENERATING', 'READY', 'FAILED']);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  QUEUED: 'Na fila',
  GENERATING: 'Gerando',
  READY: 'Pronto',
  FAILED: 'Falhou',
};

export const InsightSeveritySchema = z.enum(['INFO', 'SUCCESS', 'WARNING', 'CRITICAL']);
export type InsightSeverity = z.infer<typeof InsightSeveritySchema>;

export const InsightTypeSchema = z.enum([
  'PERFORMANCE',
  'ATTENDANCE',
  'RISK',
  'IMPROVEMENT',
  'CONTENT_RECOMMENDATION',
]);
export type InsightType = z.infer<typeof InsightTypeSchema>;

/** Classificação oficial do boletim — sempre calculada deterministicamente no backend. */
export const GradeSituationSchema = z.enum(['APPROVED', 'RECOVERY', 'ATTENTION', 'IN_PROGRESS']);
export type GradeSituation = z.infer<typeof GradeSituationSchema>;

export const GRADE_SITUATION_LABELS: Record<GradeSituation, string> = {
  APPROVED: 'Aprovado',
  RECOVERY: 'Recuperação',
  ATTENTION: 'Atenção',
  IN_PROGRESS: 'Em andamento',
};

/** Faixas de desempenho usadas em listagens (aluno/professor). */
export const PerformanceBandSchema = z.enum(['EXCELLENT', 'HIGHLIGHT', 'GOOD', 'ATTENTION', 'NO_DATA']);
export type PerformanceBand = z.infer<typeof PerformanceBandSchema>;

export const PERFORMANCE_BAND_LABELS: Record<PerformanceBand, string> = {
  EXCELLENT: 'Excelente',
  HIGHLIGHT: 'Destaque',
  GOOD: 'Bom',
  ATTENTION: 'Atenção',
  NO_DATA: 'Sem notas',
};
